import type { AdminUser } from '@/services/admin.service';

export interface UserChoice {
  userId: number;
  label: string;
}

/**
 * Appended to the one deactivated person a picker is asked to retain. Plain
 * words on purpose — this is read by front-desk staff, not by us.
 */
const INACTIVE_SUFFIX = ' (no longer active)';

/** `Lastname, Firstname`, tolerating either half being blank. */
function baseLabel(user: AdminUser): string {
  const name = `${user.lastName ?? ''}, ${user.firstName ?? ''}`.replace(/^, |, $/g, '').trim();
  if (name) return name;
  const userName = (user.userName ?? '').trim();
  return userName || `User #${user.userId}`;
}

/**
 * Staff choices for an assignee picker.
 *
 * `/admin/users` returns every row for the account, deactivated staff included
 * — soft delete on `users` is `active = 0` and the endpoint applies no filter,
 * because the Users admin grid needs those rows for its "Show deleted" toggle.
 * Every picker therefore has to drop them itself.
 *
 * `keepUserId` is the person the record is already assigned to. They are kept
 * even once deactivated: `Select` resolves its trigger label by looking the
 * value up in the options, so filtering them out renders the placeholder while
 * the assignment is still stored, which reads as unassigned and is not.
 *
 * Nothing enforces unique first/last names on `users` (only `userName` is
 * checked, and only per account), so colliding labels are told apart by the
 * sign-in name. That has to go in the label itself: `Select` renders and
 * searches `label` and nothing else.
 */
export function buildUserChoices(
  users: AdminUser[],
  keepUserId?: number | string | null,
): UserChoice[] {
  const keep =
    keepUserId === null || keepUserId === undefined || keepUserId === ''
      ? null
      : String(keepUserId);

  const kept = (users ?? []).filter(
    (u) => u.active || (keep !== null && String(u.userId) === keep),
  );

  const labelCounts = new Map<string, number>();
  for (const u of kept) {
    const label = baseLabel(u);
    labelCounts.set(label, (labelCounts.get(label) ?? 0) + 1);
  }

  return kept
    .map((u) => {
      const base = baseLabel(u);
      const userName = (u.userName ?? '').trim();
      let label = (labelCounts.get(base) ?? 0) > 1 && userName ? `${base} (${userName})` : base;
      if (!u.active) label += INACTIVE_SUFFIX;
      return { userId: u.userId, label };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}
